import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';

interface ContractorPerformance {
  contractorId: string;
  company: string;
  totalProjects: number;
  completedProjects: number;
  completionRate: number;
  averageCompletionTime: number;
  rating: number;
  totalRatings: number;
}

const ContractorPerformanceDashboard: React.FC = () => {
  const [performanceData, setPerformanceData] = useState<ContractorPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { get } = useApi();

  useEffect(() => {
    const fetchPerformanceData = async () => {
      try {
        setLoading(true);
        const response = await get('/api/admin/kpis/contractor-performance');
        if (response.success) {
          setPerformanceData(response.data);
        } else {
          setError(response.message || 'Failed to fetch performance data');
        }
      } catch (err: any) {
        setError(err.message || 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchPerformanceData();
  }, [get]);

  if (loading) return <div className="p-6">Loading performance data...</div>;
  if (error) return <div className="p-6 text-red-500">Error: {error}</div>;

  const getRatingColor = (rating: number) => {
    if (rating >= 4.5) return 'text-green-600';
    if (rating >= 3.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCompletionRateColor = (rate: number) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Contractor Performance Dashboard</h2>
      
      {performanceData.length === 0 ? (
        <div className="text-gray-600">No contractor performance data available</div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contractor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Projects
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Completion Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg. Completion Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rating
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Ratings
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {performanceData.map((contractor) => (
                  <tr key={contractor.contractorId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {contractor.company}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {contractor.completedProjects}/{contractor.totalProjects}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-semibold ${getCompletionRateColor(contractor.completionRate)}`}>
                        {contractor.completionRate.toFixed(1)}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {contractor.averageCompletionTime > 0 
                          ? `${contractor.averageCompletionTime.toFixed(0)} days`
                          : 'N/A'
                        }
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-semibold ${getRatingColor(contractor.rating)}`}>
                        {contractor.rating > 0 ? contractor.rating.toFixed(1) : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {contractor.totalRatings}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary Statistics */}
      {performanceData.length > 0 && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Average Rating</h3>
            <div className="text-3xl font-bold text-blue-600">
              {(performanceData.reduce((sum, c) => sum + c.rating, 0) / performanceData.length).toFixed(1)}
            </div>
            <p className="text-sm text-gray-600">Across all contractors</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Average Completion Rate</h3>
            <div className="text-3xl font-bold text-green-600">
              {(performanceData.reduce((sum, c) => sum + c.completionRate, 0) / performanceData.length).toFixed(1)}%
            </div>
            <p className="text-sm text-gray-600">Across all contractors</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Projects</h3>
            <div className="text-3xl font-bold text-purple-600">
              {performanceData.reduce((sum, c) => sum + c.totalProjects, 0)}
            </div>
            <p className="text-sm text-gray-600">Across all contractors</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorPerformanceDashboard;

